import { isEqual } from 'lodash';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import PropTypes from 'prop-types';
import React, { memo, useMemo, useState } from 'react';
import { Grid, TextField } from '@mui/material';
import Button from 'tailwind-components/Button/Button';
import icons from 'constants/icons';
import SimpleFilter from '../BobTableFilters/SimpleFilter/SimpleFilter';

const SearchIcon = icons['search'];

const RiskManagementTable = ({ 
  data_for_table,
  periodOptions = [],
  retentionRateYear = 'current',
  setRetentionRateYear
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Los datos ya vienen procesados del backend con retention rate
  const rawTableData = useMemo(() => {
    if (!data_for_table || data_for_table.length === 0) return [];
    return data_for_table.map(row => ({
      agentName: row.agent_name || 'Unknown',
      agentNPN: row.agent_npn || 'N/A',
      totalClients: row.total_clients || 0,
      activeClients: row.active_clients || 0,
      retentionRate: row.retention_rate || 0
    }));
  }, [data_for_table]);

  // Filter table data based on search term (agent name or NPN)
  const tableData = useMemo(() => {
    if (!searchTerm.trim()) return rawTableData;
    
    const searchLower = searchTerm.toLowerCase().trim();
    return rawTableData.filter(row => {
      const agentNameMatch = row.agentName?.toLowerCase().includes(searchLower);
      const npnMatch = row.agentNPN?.toString().toLowerCase().includes(searchLower);
      return agentNameMatch || npnMatch;
    });
  }, [rawTableData, searchTerm]);

  const columns = React.useMemo(
    () => [
      {
        header: 'Agent Name',
        accessorKey: 'agentName',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        enableResizing: false
      },
      {
        header: 'NPN',
        accessorKey: 'agentNPN',
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        enableResizing: false
      },
      /* eslint react/prop-types: 0 */
      {
        header: 'Total Clients',
        accessorKey: 'totalClients',
        size: 150,
        enableColumnFilter: false,
        enableResizing: false,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <div style={{ textAlign: 'left' }}>
              {value || 0}
            </div>
          );
        }
      },
      {
        header: 'Active Clients',
        accessorKey: 'activeClients',
        size: 150,
        enableColumnFilter: false,
        enableResizing: false,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <div
              style={{
                textAlign: 'left',
                color: value > 0 ? '#5DBB29' : '#909090'
              }}
            >
              {value || 0}
            </div>
          );
        }
      },
      {
        header: 'Retention Rate (%)',
        accessorKey: 'retentionRate',
        size: 150,
        enableColumnFilter: false,
        enableResizing: false,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const rate = parseFloat(value) || 0;
          return (
            <div
              style={{
                textAlign: 'left',
                color: rate >= 80 ? '#5DBB29' : rate >= 60 ? '#FFA500' : '#E85C53',
                fontWeight: 600
              }}
            >
              {rate.toFixed(2)}%
            </div>
          );
        }
      }
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableExpanding: false,
    enableExpandAll: false,
    manualPagination: true,
    enableColumnActions: false,
    enableColumnResizing: false,
    enableTopToolbar: false,
    enableSorting: true,
    enableRowSelection: false,
    enableStickyHeader: true,
    enablePagination: false,
    initialState: { showColumnFilters: false },
    enableRowNumbers: false,
    muiTablePaperProps: {
      elevation: 0,
      style: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 0
      }
    },
    muiTableContainerProps: {
      id: 'risk-management-summary-table-body',
      style: {
        flex: 1,
        scrollbarWidth: 'auto'
      }
    },
    renderEmptyRowsFallback: () => (
      <h2
        style={{
          color: 'grey',
          textAlign: 'center',
          fontWeight: '400',
          fontSize: 14,
          padding: 15
        }}
      >
        No Retention Rate data found
      </h2>
    )
  });

  return (
    <div
      style={{
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Grid 
        container 
        paddingLeft={2}
        paddingRight={2}
        paddingBottom={2}
        paddingTop={0}
        rowSpacing={{ xs: 1 }}
        alignItems='center'
      >
        <Grid item xs={12} sm={8} display='flex' paddingLeft={2}>
          <TextField
            fullWidth
            size='small'
            id='outlined-search-risk-management'
            label='Search by agent name or NPN'
            placeholder='Enter agent name or NPN...'
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
            }}
            type='search'
            InputProps={{
              sx: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
            }}
          />
          <Button
            text='Search'
            variant='primary'
            size='medium'
            style={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0
            }}
            icon={<SearchIcon />}
          />
        </Grid>
        {periodOptions.length > 0 && (
          <Grid
            item
            xs={12}
            sm={4}
            paddingRight={2}
            display='flex'
            justifyContent='flex-end'
            alignItems='center'
          >
            <label style={{ marginRight: '10px', fontSize: '14px' }}>
              Period:
            </label>
            <SimpleFilter
              showCount={false}
              multiple={false}
              options={periodOptions}
              value={
                retentionRateYear === 'current'
                  ? periodOptions[0]
                  : periodOptions[1]
              }
              handleChange={event => {
                const {
                  target: { value }
                } = event;
                const selectedValue = Array.isArray(value)
                  ? value[0]
                  : value;
                const yearFilter = selectedValue.startsWith('Current')
                  ? 'current'
                  : 'past';
                if (setRetentionRateYear) {
                  setRetentionRateYear(yearFilter);
                }
              }}
            />
          </Grid>
        )}
      </Grid>
      <div
        style={{
          width: '100%',
          flex: 1,
          display: 'flex'
        }}
      >
        <MaterialReactTable table={table} />
      </div>
    </div>
  );
};

RiskManagementTable.propTypes = {
  data_for_table: PropTypes.array,
  periodOptions: PropTypes.array,
  retentionRateYear: PropTypes.string,
  setRetentionRateYear: PropTypes.func
};

export default memo(RiskManagementTable, (prevProps, nextProps) =>
  isEqual(prevProps.data_for_table, nextProps.data_for_table)
);

